/* @jsx h */
import { cn } from "@lemmonhq/uikit";
import { defineComponent, h, computed } from "vue";
import { TableVisualProps } from "./types";

const CHAIR = {
  bg: "var(--floor-plan-chair-background)",
  border: "1px solid var(--floor-plan-chair-border)",
  shadow: "var(--floor-plan-chair-shadow)",
  w: 28,
  d: 11,
  radius: {
    top: "4px 4px 1px 1px",
    bottom: "1px 1px 4px 4px",
    left: "4px 1px 1px 4px",
    right: "1px 4px 4px 1px",
  } as Record<string, string>,
};

export const UnassignedTableCell = defineComponent<
  Omit<TableVisualProps, "tableStatus">
>({
  name: "UnassignedTableCell",
  props: ["planConfig", "onFloorPlanCellChange", "tables", "onTableOpen"],
  setup(props) {
    const isRound = computed(() => props.planConfig.shape === "round");

    const tableDimensions = computed(() => ({
      width: props.planConfig.colEnd - props.planConfig.colStart + 1,
      height: props.planConfig.rowEnd - props.planConfig.rowStart + 1,
    }));

    const seatCount = computed(() => {
      if (props.planConfig.seats) return props.planConfig.seats;
      const area = tableDimensions.value.width * tableDimensions.value.height;
      return isRound.value ? Math.max(3, area * 3) : Math.max(4, area * 4);
    });

    const renderRoundChairs = () => {
      const total = seatCount.value;
      // Rectangular backrests around the circle, same style as rect tables
      return Array.from({ length: total }, (_, i) => {
        const angle = (360 / total) * i - 90;
        const rad = (angle * Math.PI) / 180;
        // Place at 50% — centered on the circle edge
        const x = 50 + Math.cos(rad) * 50;
        const y = 50 + Math.sin(rad) * 50;
        return (
          <div
            key={i}
            class="absolute"
            style={{
              zIndex: 1,
              width: `${CHAIR.w}px`,
              height: `${CHAIR.d}px`,
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(-50%, -50%) rotate(${angle + 90}deg)`,
              borderRadius: "4px 4px 1px 1px",
              background: CHAIR.bg,
              border: CHAIR.border,
              boxShadow: CHAIR.shadow,
            }}
          />
        );
      });
    };

    const renderRectChairs = () => {
      const { width, height } = tableDimensions.value;
      const total = seatCount.value;
      const perimeter = 2 * (width + height);
      const maxPerEdge = (len: number) => Math.max(1, len * 2);
      const topCount = Math.min(
        maxPerEdge(width),
        Math.max(1, Math.round(total * (width / perimeter))),
      );
      const bottomCount = Math.min(
        maxPerEdge(width),
        Math.max(1, Math.round(total * (width / perimeter))),
      );
      const leftCount = Math.min(
        maxPerEdge(height),
        Math.max(1, Math.round(total * (height / perimeter))),
      );
      let rightCount = Math.max(0, total - topCount - bottomCount - leftCount);
      rightCount = Math.min(maxPerEdge(height), rightCount);

      const edge = (
        edgeName: "top" | "bottom" | "left" | "right",
        count: number,
      ) => {
        const isV = edgeName === "left" || edgeName === "right";
        const pos: Record<string, Record<string, string>> = {
          top: {
            top: "0",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
          },
          bottom: {
            bottom: "0",
            left: "50%",
            transform: "translateX(-50%) translateY(50%)",
          },
          left: {
            left: "0",
            top: "50%",
            transform: "translateY(-50%) translateX(-50%)",
          },
          right: {
            right: "0",
            top: "50%",
            transform: "translateY(-50%) translateX(50%)",
          },
        };
        return (
          <div
            class={cn(
              "absolute flex justify-evenly items-center",
              isV ? "flex-col" : "flex-row",
            )}
            style={{
              zIndex: 1,
              ...(isV
                ? { height: `calc(100% - ${CHAIR.d + 4}px)` }
                : { width: `calc(100% - ${CHAIR.w + 4}px)` }),
              ...pos[edgeName],
            }}
          >
            {Array.from({ length: count }, (_, i) => (
              <div
                key={i}
                style={{
                  width: `${isV ? CHAIR.d : CHAIR.w}px`,
                  height: `${isV ? CHAIR.w : CHAIR.d}px`,
                  flexShrink: "0",
                  borderRadius: CHAIR.radius[edgeName],
                  background: CHAIR.bg,
                  border: CHAIR.border,
                  boxShadow: CHAIR.shadow,
                }}
              />
            ))}
          </div>
        );
      };

      return [
        edge("top", topCount),
        edge("bottom", bottomCount),
        edge("left", leftCount),
        edge("right", rightCount),
      ];
    };

    return () => {
      const round = isRound.value;
      return (
        <div class="relative w-full h-full select-none">
          {/* Rect chairs — before table surface */}
          {!round && renderRectChairs()}

          {/* Table surface — same style as assigned free tables */}
          <div
            class={cn(
              "w-full h-full flex flex-col items-center justify-center relative overflow-hidden",
              round ? "rounded-full" : "rounded-lg",
            )}
            style={{
              zIndex: 2,
              background: "var(--floor-plan-table-free-background)",
              boxShadow: "var(--floor-plan-table-free-shadow)",
              color: "var(--floor-plan-table-free-content)",
              cursor: "pointer",
            }}
          >
            <span
              class="material-symbols-outlined select-none"
              style="font-size: clamp(0.7rem, 2cqi, 1.2rem); opacity: 0.4;"
            >
              link_off
            </span>
          </div>

          {/* Round chairs — after table surface */}
          {round && renderRoundChairs()}
        </div>
      );
    };
  },
});
